// Pricing tiers + Stripe billing, backed by the Go backend's /billing/*
// routes via the same /api-auth proxy prefix authApi.ts / profileApi.ts use.
//
// - GET  /billing/tiers            public catalog (no auth)
// - GET  /billing/me               caller's plan + entitlements + usage
// - POST /billing/checkout-session start a Stripe Checkout (returns {url})
// - POST /billing/portal-session   Stripe billing portal (returns {url})
//
// The mutations need the double-submit CSRF header (same as syncProfile);
// the session itself rides the httpOnly cookie via credentials: 'include'.
import type { BillingInterval, BillingState, TierCatalogEntry } from '../types';
import { readCsrfCookie, reportHttpStatus } from './dataApi';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

async function billingGet<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  return unwrap<T>(response);
}

async function billingPost<T>(path: string, body?: unknown): Promise<T> {
  const csrf = readCsrfCookie();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body ?? {}),
  });
  return unwrap<T>(response);
}

async function unwrap<T>(response: Response): Promise<T> {
  let parsed: any = null;
  try {
    parsed = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = parsed?.data;
  if (response.ok && data && typeof data === 'object' && !('error' in data)) {
    return data as T;
  }
  const message =
    (data && typeof data === 'object' && data.error) || `Request failed (${response.status})`;
  throw new Error(String(message));
}

export async function fetchTierCatalog(): Promise<TierCatalogEntry[]> {
  const data = await billingGet<{ tiers: TierCatalogEntry[] }>('/billing/tiers');
  return Array.isArray(data.tiers) ? data.tiers : [];
}

export async function fetchBillingState(): Promise<BillingState> {
  return billingGet<BillingState>('/billing/me');
}

// Returns the Stripe Checkout URL - the caller should redirect the browser
// to it (window.location.assign).
export async function startCheckout(
  tierId: string,
  interval: BillingInterval = 'monthly'
): Promise<string> {
  const data = await billingPost<{ url: string }>('/billing/checkout-session', { tierId, interval });
  return data.url;
}

// Returns the Stripe billing-portal URL (manage payment method / change /
// cancel). Only works once the account has an active Stripe customer.
export async function openBillingPortal(): Promise<string> {
  const data = await billingPost<{ url: string }>('/billing/portal-session');
  return data.url;
}
